import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import {
  Box,
  HStack,
  Text,
  VStack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useToast } from "../../hooks/useToast";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { EditVenueForm } from "../../forms/EditVenueForm";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { CopyText } from "../../components/Utilities/CopyText";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

interface VenueDetailProps {
  create?: boolean;
}

export const VenueDetail = (props: VenueDetailProps) => {
  const [searchParams] = useSearchParams();
  const { businessId, venueId } = useParams();
  const [venue, setVenue] = useState<CherryHubAdminApi.VenueDocument | null>();
  const business = useBusinessContext();
  const navigate = useNavigate();
  const { data: venueDoc, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !venueId) {
        return null;
      }
      return apiClient.getBusinessVenue(businessId, venueId);
    },
    [businessId, venueId]
  );
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  useEffect(() => {
    setVenue(venueDoc);
  }, [venueDoc]);

  const onSuccess = useCallback(
    (venue: CherryHubAdminApi.VenueDocument) => {
      setVenue(venue);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Venue created.");
        navigate(
          `/admin/businesses/${business.BusinessId}/venues/${venue.Meta?.Id}`
        );
      } else {
        showSuccessToast("Venue updated.");
      }
    },
    [showSuccessToast, venue]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Venue update failed.");
    },
    [showErrorToast]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Venue
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/venues`}
              >
                Venues
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/venues/${
                  props.create ? "create" : venue?.id
                }`}
              >
                {props.create ? "New" : venue?.DisplayName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || !venue) && !props.create && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="space-between">
                <Skeleton w="320px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
              <SkeletonText w="100%" mt="4" noOfLines={4} spacing="4" />
            </VStack>
          )}
          {!isLoading && editMode && (
            <EditVenueForm
              venue={venue}
              apiClient={apiClient}
              business={business}
              onCancel={() => {
                props.create
                  ? navigate(`/admin/businesses/${business.BusinessId}/venues`)
                  : setEditMode(false);
              }}
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}
          {!isLoading && !editMode && venue && (
            <>
              <HStack w="100%" pb={4}>
                <Box flexGrow={1}>
                  <CopyText text={venue?.Meta?.Id!} hover adjustMargin>
                    <Text fontSize="md" color="cherryUi.600">
                      {venue?.Meta?.Id} / {venue?.VenueNo}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{venue?.DisplayName}</b>
                  </Text>
                </Box>
                <Box>
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>

              <ViewControl
                label="Enabled"
                value={venue?.Enabled ? "Yes" : "No"}
              />

              <ViewControl
                label="Loyalty Integration Id"
                value={venue?.DefaultLoyaltyIntegrationId}
              />

              <ViewControl
                label="Membership Integration Id"
                value={venue?.DefaultMembershipIntegrationId}
              />

              <ViewControl
                label="Gaming Integration Id"
                value={venue?.DefaultGamingIntegrationId}
              />

              <ViewControl
                label="Allow Member Portal Creation"
                value={venue?.AllowMemberPortalCreation ? "Yes" : "No"}
              />

              <ViewControl
                label="Member Portal Required Fields"
                value={venue?.MemberPortalRequiredFields?.join(", ") || "None"}
              />

              <ViewControl
                label="Minimum Transaction Hold TTL Seconds"
                value={
                  venue?.PointsTransactionConfiguration
                    ?.MinimumHoldTransactionTimeToLiveSeconds
                }
              />

              <ViewControl
                label="Maximum Transaction Hold TTL Seconds"
                value={
                  venue?.PointsTransactionConfiguration
                    ?.MaximumHoldTransactionTimeToLiveSeconds
                }
              />

              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Points Type Id</Th>
                    <Th>Display Name</Th>
                    <Th>Dollar Ratio</Th>
                    <Th width="0.1%" whiteSpace="nowrap">
                      Default
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(venue?.PointsTypes?.length &&
                    venue?.PointsTypes?.map((item, index) => {
                      return (
                        <Tr key={index}>
                          <Td w="20%">{item.PointsTypeId}</Td>
                          <Td>{item.DisplayName}</Td>
                          <Td w="20%">{item.PointsToDollarRatio}</Td>
                          <Td
                            width="0.1%"
                            whiteSpace="nowrap"
                            textAlign="center"
                          >
                            {item.IsDefault ? "Yes" : "No"}
                          </Td>
                        </Tr>
                      );
                    })) || (
                    <Tr>
                      <Td colSpan={3} textAlign="center">
                        <i>No Point Types Configured</i>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
